import React from 'react'
import {
  Box,
  Divider,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  makeStyles,
} from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  phaseBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  phaseHeader: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  labelField: {
    flex: 1,
    maxWidth: 140,
  },
  titleField: {
    flex: 3,
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  bannerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statRow: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}))

const StartupEditor = ({ data, onChange }) => {
  const classes = useStyles()
  const phases = data.phases || []
  const banner = data.bottomBanner || {}

  const handlePhaseChange = (index, field, value) => {
    const updated = phases.map((phase, i) =>
      i === index ? { ...phase, [field]: value } : phase
    )
    onChange('phases', updated)
  }

  const handleTasksChange = (index, rawValue) => {
    const tasks = rawValue.split('\n')
    handlePhaseChange(index, 'tasks', tasks)
  }

  const tasksToString = (tasks) => {
    if (!tasks) return ''
    if (Array.isArray(tasks)) return tasks.join('\n')
    return String(tasks)
  }

  const handleBannerChange = (field, value) => {
    onChange('bottomBanner', { ...banner, [field]: value })
  }

  const handleBannerStatChange = (index, field, value) => {
    const stats = [...(banner.stats || [])]
    stats[index] = { ...stats[index], [field]: value }
    handleBannerChange('stats', stats)
  }

  return (
    <Box className={classes.root}>
      <Typography variant="subtitle2">Startup Phases</Typography>

      {phases.map((phase, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Divider className={classes.divider} />}
          <Box className={classes.phaseBlock}>
            <Typography variant="caption" color="textSecondary">
              Phase {index + 1}
            </Typography>
            <Box className={classes.phaseHeader}>
              <TextField
                label="Label"
                value={phase.label || ''}
                onChange={(e) =>
                  handlePhaseChange(index, 'label', e.target.value)
                }
                size="small"
                variant="outlined"
                fullWidth
                className={classes.labelField}
              />
              <TextField
                label="Title"
                value={phase.title || ''}
                onChange={(e) =>
                  handlePhaseChange(index, 'title', e.target.value)
                }
                size="small"
                variant="outlined"
                fullWidth
                className={classes.titleField}
              />
            </Box>
            <TextField
              label="Tasks (one per line)"
              value={tasksToString(phase.tasks)}
              onChange={(e) => handleTasksChange(index, e.target.value)}
              size="small"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
            />
          </Box>
        </React.Fragment>
      ))}

      <Divider className={classes.divider} />

      <Typography variant="subtitle2">Bottom Banner</Typography>
      <Box className={classes.bannerSection}>
        <FormControlLabel
          control={
            <Switch
              checked={banner.enabled !== false}
              onChange={(e) => handleBannerChange('enabled', e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label="Show banner"
        />
        {banner.enabled !== false && (
          <>
            <TextField
              label="Banner Text"
              value={banner.text || ''}
              onChange={(e) => handleBannerChange('text', e.target.value)}
              size="small"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
            />
            <Typography variant="caption" color="textSecondary">Stats</Typography>
            {(banner.stats || []).map((stat, i) => (
              <Box className={classes.statRow} key={i}>
                <TextField
                  label="Value"
                  value={stat.value || ''}
                  onChange={(e) => handleBannerStatChange(i, 'value', e.target.value)}
                  size="small"
                  variant="outlined"
                  style={{ flex: 1 }}
                />
                <TextField
                  label="Label"
                  value={stat.label || ''}
                  onChange={(e) => handleBannerStatChange(i, 'label', e.target.value)}
                  size="small"
                  variant="outlined"
                  style={{ flex: 2 }}
                />
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  )
}

export default StartupEditor
